import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  DataGrid,
  GridColumns,
  GridRenderCellParams,
  GridToolbar,
} from "@mui/x-data-grid";
import Chip from "@mui/material/Chip";

import { useErrorContext } from "../context/errorcontext";

import GridDX from "../components/layout/griddx";
import ButtonDX from "../components/controls/buttondx";
import {
  DATETIME_FORMAT,
  getFundName,
  getReqeustStatus,
  zeroPad,
} from "../shared/global";
import moment from "moment";
import { useRedemptionService } from "../shared/services/redemptionservice";

const Redemptions = () => {
  const columns: GridColumns = [
    {
      field: "requestDate",
      headerName: "Request Date",
      width: 180,
      headerAlign: "center",
      valueFormatter: ({ value }) => moment(value).format(DATETIME_FORMAT),
    },
    {
      field: "userName",
      headerName: "User Name",
      headerAlign: "center",
      width: 180,
    },
    {
      field: "cnic",
      headerName: "CNIC",
      headerAlign: "center",
      align: "center",
      width: 150,
    },
    {
      field: "folioNumber",
      headerName: "Folio",
      headerAlign: "center",
      align: "center",
      width: 100,
      valueFormatter: ({ value }) => zeroPad(value, 7),
    },
    {
      field: "fundId",
      headerName: "Fund Name",
      headerAlign: "center",
      width: 300,
      valueFormatter: ({ value }) => getFundName(value),
    },
    {
      field: "redemptionAmount",
      headerName: "Amount",
      headerAlign: "center",
      width: 150,
      align: "right",
      valueFormatter: ({ value }) =>
        Intl.NumberFormat("en-us", {
          maximumFractionDigits: 2,
          minimumFractionDigits: 2,
        }).format(value),
    },
    {
      field: "requestStatus",
      headerName: "Status",
      headerAlign: "center",
      width: 150,
      align: "center",
      valueFormatter: ({ value }) => getReqeustStatus(value)?.value,
      renderCell: (params: GridRenderCellParams) => {
        return drawStatusChip(params.row.requestStatus);
      },
    },
    {
      field: "action",
      headerName: "Action",
      headerAlign: "center",
      sortable: false,
      disableExport: true,
      width: 150,
      align: "center",
      renderCell: (params: GridRenderCellParams) => {
        const onClick = (e: any) => {
          return navigate("/reddetails/" + params.row.redemptionRequestId);
        };

        return (
          <ButtonDX variant="outlined" size="small" onClick={onClick}>
            View
          </ButtonDX>
        );
      },
    },
  ];

  const navigate = useNavigate();
  const { setError, setInfo } = useErrorContext();
  const { getAllRedemptionRequests } = useRedemptionService();

  const [pageSize, setPageSize] = useState(10);
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setIsLoading(true);

    getAllRedemptionRequests()
      .then((redempts) => {
        setRows(redempts);
      })
      .catch((ex) => setError(ex))
      .finally(() => setIsLoading(false));
  }, []);

  const drawStatusChip = (statusId: number) => {
    switch (statusId) {
      case 1:
        return (
          <Chip
            variant="outlined"
            label={getReqeustStatus(statusId)?.value}
            size="small"
            color="info"
          />
        );
      case 2:
        return (
          <Chip
            variant="outlined"
            label={getReqeustStatus(statusId)?.value}
            size="small"
            color="warning"
          />
        );
      case 3:
        return (
          <Chip
            variant="outlined"
            label={getReqeustStatus(statusId)?.value}
            size="small"
            color="success"
          />
        );
      case 4:
        return (
          <Chip
            variant="outlined"
            label={getReqeustStatus(statusId)?.value}
            size="small"
            color="error"
          />
        );

      default:
        return (
          <Chip
            variant="outlined"
            label={getReqeustStatus(statusId)?.value}
            size="small"
          />
        );
    }
  };

  return (
    <GridDX container sx={{ width: "100%" }}>
      <GridDX item xs={12}>
        <DataGrid
          getRowId={(row) => row.redemptionRequestId}
          rows={rows}
          columns={columns}
          pageSize={pageSize}
          loading={isLoading}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          rowsPerPageOptions={[5, 10, 15, 20]}
          disableColumnFilter
          disableColumnSelector
          disableDensitySelector
          components={{ Toolbar: GridToolbar }}
          componentsProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: { debounceMs: 500 },
            },
          }}
          initialState={{
            sorting: {
              sortModel: [{ field: "requestDate", sort: "desc" }],
            },
          }}
        />
      </GridDX>
    </GridDX>
  );
};

export default Redemptions;
